// src/components/Modelos/ModeloIndex.tsx

import React, { useState, useEffect, useRef } from "react";
import * as S from "./Modelo.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import axios, { AxiosError } from "axios";
import { ApiError } from "../../../services/apiError";
import { apiService } from "../../../services/apiService";
import { Modelo } from "../../../dtos/Modelo";
import { Fabricante } from "../../../dtos/Fabricante";
import { useNavigate } from "react-router-dom";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

const ModeloIndex = () => {
  const [dataSource, setDataSource] = useState<Modelo[]>([]);
  const [fabricantes, setFabricantes] = useState<Fabricante[]>([]); // Estado para los fabricantes
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<Modelo> | null>(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Modelo | null>(null);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance | null>(null);
  const dataGridRef = useRef<any>(null);
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Modelos");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Modelos.xlsx",
    );
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const [modelosRes, fabricantesRes] = await Promise.all([
        apiService.get<Modelo[]>("/api/Catalogos/Modelos"),
        apiService.get<Fabricante[]>("/api/Catalogos/Fabricantes"),
      ]);
      setDataSource(modelosRes);
      setFabricantes(fabricantesRes);
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setEditingItem({ isDeleted: false });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: Modelo) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: Modelo) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.patch(
          `/api/Catalogos/Modelos/${itemToDelete.id}/cambiastatus`,
          "",
        );
        fetchData();
        notify("Cambio de estatus realizado con éxito.", "success", 2000);
      } catch (error) {
        notify("Error al cambiar el estatus del registro.", "error", 3000);
      } finally {
        setIsDeletePopupVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = form.option("formData") as Modelo;

    try {
      if (formData.id) {
        // Actualizar (PUT)
        const response = await apiService.put<Modelo, Modelo>(
          `/api/Catalogos/Modelos/${formData.id}`,
          formData,
        );
        setDataSource((prev) =>
          prev.map((item) => (item.id === formData.id ? response : item)),
        );
        notify("Modelo actualizado con éxito.", "success", 2000);
      } else {
        // Crear (POST)
        const response = await apiService.post<Modelo, Modelo>(
          "/api/Catalogos/Modelos",
          formData,
        );
        setDataSource((prev) => [...prev, response]);
        notify("Modelo creado con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
      fetchData(); // Recargar datos para reflejar cambios
    } catch (error) {
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = !cellInfo.value; // isDeleted: false es Activo
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as Modelo;
    const isActive = !item.isDeleted;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(item)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      {/* CAMBIO 1: Activa los estilos globales para el título */}
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo de modelos</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="id"
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <Paging defaultPageSize={10} />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} allowSearch={true} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir Modelo",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        <Column dataField="name" caption="Nombre del Modelo" width={300} />
        <Column dataField="fabricante" caption="Fabricante" width={400} />
        <Column
          dataField="isDeleted"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={300}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={300}
          cellRender={ActionsCell}
          allowExporting={false}
        />
      </DataGrid>

      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem?.id
            ? `Editar Modelo: ${editingItem.name}`
            : "Añadir nuevo modelo"
        }
        width={500}
        height="auto"
        className="custom-form-popup" // <-- Clase para que funcionen los estilos
        // CAMBIO 2: Se elimina por completo la propiedad 'toolbarItems'
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="name">
              <Label text="Nombre del Modelo" />
              <RequiredRule message="El nombre es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="fabricanteId"
              editorType="dxSelectBox"
              editorOptions={{
                items: fabricantes,
                displayExpr: "nombre",
                valueExpr: "id",
                searchEnabled: true,
              }}
            >
              <Label text="Fabricante" />
              <RequiredRule message="El fabricante es requerido." />
            </SimpleItem>
          </Form>
        </S.FormContainer>

        {/* CAMBIO 3: Se añade el footer personalizado con nuestros botones */}
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      {/* El popup de confirmación se queda igual */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle={true}
        title={`Confirmar ${
          !itemToDelete?.isDeleted ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${
                !itemToDelete?.isDeleted ? "Desactivar" : "Activar"
              }`,
              type: !itemToDelete?.isDeleted ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmDelete,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {!itemToDelete?.isDeleted ? "desactivar" : "activar"}
            </strong>{" "}
            el modelo
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.name}"?
          </p>
        </div>
      </Popup>
    </S.PageWrapper>
  );
};

export default ModeloIndex;
